import React from 'react';
import { 
  Typography, 
  Box, 
  Card, 
  CardHeader, 
  CardContent, 
  Grid,
  Chip,
  Paper,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useLocation, useNavigate } from 'react-router-dom';

const ActionsGenerateWidget = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'generatewidget',
    onSaveStatusChange,
    customComparison: () => true, // No form data to compare
    customSaveData: () => ({
      // Preserve the tag data when saving
      tag: actionSettings.initialSettings?.current?.tag || ''
    }),
    loadDataFunction: () => {} // No data to load
  });

  const handleOpenWidgetSettings = () => {
    const widgetTag = actionSettings.initialSettings?.current?.tag;
    if (!caseid || !widgetTag) {
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('openWidgetSettings', 'true');
    nextParams.set('widgetTag', widgetTag);
    navigate(`${location.pathname}?${nextParams.toString()}`);
  };

  return (
    <ActionSettingsLayout
      title="Update Widget"
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={actionSettings.handleSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      primaryColor="#2e7d2e"
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #2e7d2e10',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#2e7d2e',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(46, 125, 46, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <WidgetsIcon sx={{ fontSize: '1rem' }} />
                    WIDGET
                  </Box>
                  {t('generate_widget.tag_label', 'Widget Configuration')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                p: { xs: 1.5, sm: 2 },
              }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ 
                  color: '#666', 
                  mb: 3,
                  fontWeight: 500 
                }}>
                  {t('generate_widget.tag_description', 'Widget Tag:')}
                </Typography>
                
                {actionSettings.initialSettings?.current?.tag ? (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      borderRadius: '12px',
                      backgroundColor: '#f8fdf8',
                      border: '2px solid #2e7d2e20',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '200px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f0fdf0',
                        border: '2px solid #2e7d2e40',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px rgba(46, 125, 46, 0.1)',
                      }
                    }}
                  >
                    <Tooltip title={t('generate_widget.open_widget_settings', 'Open widget settings')} arrow>
                      <Chip
                        icon={<WidgetsIcon />}
                        label={actionSettings.initialSettings.current.tag}
                        clickable
                        onClick={handleOpenWidgetSettings}
                        sx={{
                          backgroundColor: '#2e7d2e',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '1rem',
                          height: '40px',
                          px: 1,
                          cursor: 'pointer',
                          '& .MuiChip-icon': {
                            color: 'white',
                            fontSize: '1.1rem'
                          },
                          '& .MuiChip-label': {
                            px: 2
                          },
                          '&:hover': {
                            backgroundColor: '#2c6f2c'
                          }
                        }}
                      />
                    </Tooltip>
                  </Paper>
                ) : (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 4,
                      borderRadius: '12px',
                      backgroundColor: '#f9f9f9',
                      border: '2px dashed #ddd',
                      textAlign: 'center',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <WidgetsIcon sx={{ 
                      fontSize: '3rem', 
                      color: '#ccc',
                      mb: 2 
                    }} />
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      mb: 1
                    }}>
                      {t('generate_widget.no_tag_title', 'No Tag Configured')}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      opacity: 0.8
                    }}>
                      {t('generate_widget.no_tag_description', 'A tag will be automatically assigned when this action runs.')}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsGenerateWidget);
